/**
 * MaterialsManager — PBR material management + KHR_materials_variants support.
 *
 * Responsibilities:
 *  - Reflect variant switching requests onto TryonScene
 *  - Provide texture URL override (replaces the color map on all meshes)
 *  - Cache loaded textures in a Map keyed by URL to avoid redundant GPU uploads
 *  - Expose getVariantNames() for catalog UI binding
 *
 * This class is a thin coordination layer on top of TryonScene's built-in
 * variant infrastructure. It adds:
 *   1. A named-variant API independent of mesh traversal details
 *   2. Texture URL overrides (for per-variant colour images)
 *   3. Texture resource tracking so destroy() can clean up GPU memory
 *
 * Usage:
 *   const mat = new MaterialsManager(scene);
 *   mat.switchVariant('Matte Black');
 *   await mat.applyTextureOverride('https://cdn/black.jpg', 'color');
 *   mat.destroy();
 */

import type { Object3D, Mesh, Material, MeshStandardMaterial, Texture, Scene } from 'three';
import type { IMaterialMutationScope } from '@mirrably/renderer';

/**
 * Typed contract for the scene object consumed by MaterialsManager.
 *
 * Previously typed as `TryonScene` (concrete class), which forced an `as any`
 * cast when wiring up ProductViewerScene (A5 N5). Using a structural interface
 * here lets both TryonScene and ProductViewerScene satisfy the contract without
 * coupling MaterialsManager to either implementation.
 */
interface ISceneContext {
  getState(): IMaterialMutationScope | null;
  getScene(): Scene | null;
  getVariantNames(): string[];
  switchVariant(name: string): void;
  readonly isInitialized: boolean;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Texture override slot on a MeshStandardMaterial. */
export type TextureSlot = 'color' | 'normal' | 'metalRough' | 'bump' | 'metalness' | 'roughness' | 'alpha' | 'ao' | 'emissive';

const SLOT_TO_PROPERTY: Record<TextureSlot, string> = {
  color: 'map',
  normal: 'normalMap',
  metalRough: 'metalnessMap', // legacy slot
  bump: 'bumpMap',
  metalness: 'metalnessMap',
  roughness: 'roughnessMap',
  alpha: 'alphaMap',
  ao: 'aoMap',
  emissive: 'emissiveMap',
};

/** Per-mesh material override definition. */
export interface MeshMaterialOverride {
  color?: string;
  bumpScale?: number;
  aoMapIntensity?: number;
  depthWrite?: boolean;
  textures?: Record<string, string>;
}

/** A loaded Three.js texture (typed as unknown until dynamic import resolves). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyTexture = any;

// ---------------------------------------------------------------------------
// MaterialsManager
// ---------------------------------------------------------------------------

export class MaterialsManager {
  private readonly scene: ISceneContext;

  /** URL → loaded Three.js Texture. Avoids duplicate GPU uploads. */
  private readonly textureCache = new Map<string, AnyTexture>();

  /** Textures we own and must dispose on destroy(). */
  private readonly ownedTextures: AnyTexture[] = [];

  /** Active variant name; null means use GLB-authored materials. */
  private activeVariant: string | null = null;

  constructor(scene: ISceneContext) {
    this.scene = scene;
  }

  // ── Variant API ─────────────────────────────────────────────────────────

  /**
   * Activate a named material variant defined in the loaded GLB's
   * KHR_materials_variants extension.
   *
   * If the variant name is not found in the model, the call is a no-op and a
   * warning is logged. The scene state is not mutated.
   *
   * @param name - Variant name (case-sensitive, must match GLB metadata).
   */
  switchVariant(name: string): void {
    const available = this.scene.getVariantNames();

    if (!available.includes(name)) {
      console.warn(
        `[MaterialsManager] Variant "${name}" not found. ` +
          `Available: ${available.join(', ') || '(none)'}`,
      );
      return;
    }

    this.scene.switchVariant(name);
    this.activeVariant = name;
  }

  /**
   * Returns the names of all available variants in the currently loaded model.
   * Returns an empty array if no model is loaded or the model has no variants.
   */
  getVariantNames(): string[] {
    return this.scene.getVariantNames();
  }

  /**
   * Returns the currently active variant name, or null if using authored
   * (default) materials.
   */
  getActiveVariant(): string | null {
    return this.activeVariant;
  }

  // ── Texture override ────────────────────────────────────────────────────

  /**
   * Apply a texture URL override to the specified slot on all loaded meshes.
   *
   * Textures are cached by URL — re-applying the same URL is a no-op after
   * the first call. This is intended for per-variant colour images served from
   * a CDN (e.g. when the GLB uses atlas textures that need runtime overrides).
   *
   * @param url  - URL of the image to load as a texture.
   * @param slot - Material slot to replace.
   */
  async applyTextureOverride(url: string, slot: TextureSlot, meshName?: string): Promise<void> {
    if (!this.scene.isInitialized) {
      throw new Error('[MaterialsManager] applyTextureOverride() called before scene.init().');
    }

    // Load (or reuse cached) texture
    const texture = await this._loadTexture(url);

    // Apply to meshes in the scene via traversal
    const sceneObj = this.scene.getScene();
    if (!sceneObj) return;

    const THREE = await import('three');
    const prop = SLOT_TO_PROPERTY[slot];

    sceneObj.traverse((obj: Object3D) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const mesh = obj as Mesh;

      // When meshName is provided, only apply to matching meshes
      if (meshName !== undefined && mesh.name !== meshName) return;

      const applyToMat = (mat: Material) => {
        if (!(mat instanceof THREE.MeshStandardMaterial)) return;
        const stdMat = mat as MeshStandardMaterial;
        if (prop) {
          (stdMat as unknown as Record<string, unknown>)[prop] = texture as Texture;
        }
        stdMat.needsUpdate = true;
      };

      if (Array.isArray(mesh.material)) {
        (mesh.material as Material[]).forEach(applyToMat);
      } else if (mesh.material) {
        applyToMat(mesh.material as Material);
      }
    });
  }

  /**
   * Apply per-mesh material overrides (color, bump scale, textures, etc.).
   *
   * @param overrides - Map of mesh name to override definition.
   */
  async applyMeshOverrides(overrides: Record<string, MeshMaterialOverride>): Promise<void> {
    const state = this.scene.getState();
    if (!state?.gltf || !state.THREE) return;
    const { THREE } = state;

    const texturePromises: Promise<void>[] = [];

    state.gltf.scene.traverse((obj: Object3D) => {
      if (!(obj instanceof (THREE.Mesh as unknown as typeof Mesh))) return;
      const mesh = obj as Mesh;
      const override = overrides[mesh.name];
      if (!override) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mat = mesh.material as any;
      if (!mat) return;

      if (override.color) mat.color?.set(override.color);
      if (override.bumpScale !== undefined) mat.bumpScale = override.bumpScale;
      if (override.aoMapIntensity !== undefined) mat.aoMapIntensity = override.aoMapIntensity;
      if (override.depthWrite !== undefined) mat.depthWrite = override.depthWrite;

      if (override.textures) {
        for (const [slot, url] of Object.entries(override.textures)) {
          if (url && SLOT_TO_PROPERTY[slot as TextureSlot]) {
            texturePromises.push(this.applyTextureOverride(url, slot as TextureSlot, mesh.name));
          }
        }
      }
      mat.needsUpdate = true;
    });

    await Promise.all(texturePromises);
  }

  // ── Cleanup ─────────────────────────────────────────────────────────────

  /**
   * Dispose all textures loaded by this manager and reset state.
   *
   * Does NOT dispose materials or the scene — those are owned by TryonScene.
   */
  destroy(): void {
    for (const texture of this.ownedTextures) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      texture?.dispose?.();
    }
    this.ownedTextures.length = 0;
    this.textureCache.clear();
    this.activeVariant = null;
  }

  // ── Private helpers ─────────────────────────────────────────────────────

  /**
   * Load a texture from a URL, returning a cached copy if available.
   *
   * Uses Three.js TextureLoader via dynamic import so Three.js remains
   * a runtime-only dependency.
   */
  private async _loadTexture(url: string): Promise<AnyTexture> {
    const cached = this.textureCache.get(url);
    if (cached) return cached;

    const { TextureLoader, SRGBColorSpace } = await import('three');
    const loader = new TextureLoader();
    const texture = await loader.loadAsync(url);
    texture.colorSpace = SRGBColorSpace;

    this.textureCache.set(url, texture);
    this.ownedTextures.push(texture);

    return texture;
  }
}
